import React, { useMemo } from "react";
import { BotData } from "@shared/types";
import Text from "common/Typography/Text";
import styled from "styled-components";
import OrderNumber from "common/Numbering/OrderNumber";
import {
  BotBulkActionData,
  Error,
} from "@shared/features/bots/BotForm/common/types";
import { isSymbol } from "@shared/types/utils";
import Popover from "common/Popover/Popover.tsx";
import Title from "common/Typography/Title.tsx";
import BottomDrawer from "common/Drawers/BottomDrawer.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import Link from "common/Typography/Link.tsx";

interface BotSummaryProps {
  bot: BotBulkActionData;
  index: number;
  isValid?: boolean;
  onClick?: (bot: BotData) => void;
}

const BotSummary = ({ bot, isValid, index, onClick }: BotSummaryProps) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isBulkEditBotData = "id" in bot;
  const errors = bot.validationData.errors;

  const getFirstColumn = () => {
    if (isBulkEditBotData) return bot.name;
    const account = bot.validationData.parsedData?.account;
    return isSymbol(account) ? undefined : account?.client_name;
  };

  const helperTitle = "Bot Configuration Issues";
  const errorTexts = [
    ...(errors?.map((error) => ERROR_TO_DISPLAY[error]) || []),
    ...(bot.validationData.errorTexts || []),
  ];

  const helperContent = errorTexts.length ? (
    <>
      <Text type="medium">
        This bot cannot be created due to the following issues:
      </Text>
      <StyledList>
        {errorTexts?.map((errorText, index) => {
          return (
            <ErrorText key={errorText}>
              {index + 1}. {errorText}
            </ErrorText>
          );
        })}
      </StyledList>
    </>
  ) : undefined;

  const hoverContent = useMemo(() => {
    if (!errorTexts?.length) return;
    return (
      <div style={{ maxWidth: 400 }}>
        <Title level={3}>{helperTitle}</Title>
        {helperContent}
      </div>
    );
  }, [bot]);

  const getShowIssuesTrigger = () => {
    if (isValid) return;
    const showIssuesTrigger = (
      <Link style={{ paddingLeft: 8, whiteSpace: "nowrap" }}>Show Issues</Link>
    );
    if (isBottomNavigator)
      return (
        <BottomDrawer
          title={helperTitle}
          content={helperContent}
          triggerElement={showIssuesTrigger}
          showClose={true}
        />
      );
    return (
      <Popover content={hoverContent} placement="topRight">
        {showIssuesTrigger}
      </Popover>
    );
  };

  return (
    <RowWrapper>
      <NameCol onClick={() => isBulkEditBotData && onClick?.(bot)}>
        <OrderNumber displayNumber={index + 1} />
        <NameText
          type={isValid ? undefined : "warning"}
          $clickable={isBulkEditBotData && !isBottomNavigator}
        >
          {getFirstColumn()}
        </NameText>
      </NameCol>
      {getShowIssuesTrigger()}
    </RowWrapper>
  );
};

export default React.memo(BotSummary);

const ERROR_TO_DISPLAY = {
  [Error.GENERAL_AMOUNT]: "Invalid bot amount",
  [Error.DEFAULT_STOP]: "Invalid default stop",
  [Error.DEFAULT_TAKE_PROFIT]: "Invalid default take-profit",
  [Error.INVALID_LEVERAGE]:
    "Invalid leverage (direction, margin type or multiplier)",
  [Error.INVALID_SYMBOL]: "Invalid symbol",
  [Error.EXCHANGES]: "Invalid exchanges",
  [Error.EXCHANGES_INVALID_FOR_SYMBOL]:
    "The selected symbol is invalid for one or more of the selected exchanges",
  [Error.GRIDS_DATA]: "Invalid grids data",
  [Error.ENTRY_STRATEGY]: "Invalid entry strategy",
  [Error.TAKE_PROFIT_STRATEGY]: "Invalid take-profit strategy",
  [Error.STOP_TYPE]: "Invalid stop type",
};

const StyledList = styled.div`
  margin-top: 8px;
`;
const RowWrapper = styled.div`
  width: 100%;
  padding: 2px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const NameCol = styled.div`
  display: flex;
  align-items: center;
  padding-right: 8px;
`;
const NameText = styled(Text)<{ $clickable?: boolean }>`
  ${(p) =>
    p.$clickable &&
    `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `}
`;
const ErrorText = styled(Text)`
  border-left: 2px solid ${(p) => p.theme.colors.red};
  color: ${(p) => p.theme.colors.red};
  padding-left: 12px;
`;
